import { useState, useMemo } from 'react';
import {
  fetchTopChartsMobileUP,
  fetchTopChartsPCConsoleUP,
} from '@/pages/intelligence/services/topcharts';
import { TOPGAMENUM } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { TableSelectionType } from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import moment from 'moment';
import { RecordItemType } from '@/pages/intelligence/components/SearchRecent/type';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { tranFormSenorTowerTagParam } from '@/pages/intelligence/utils/topchartsUtils';
import { useMobileOptionList } from '@/pages/intelligence/pages/TopCharts/_hooks/useMobileOptionList';
import {
  handleTableDefaultSelectGames,
  filterGamesWithRank,
} from '@/pages/intelligence/utils/tableUtils';
import {
  updateShortLinkFilterId,
  updateTimeFilterValue,
  URL_FILTER_KEY,
} from '@intelligence/utils/urlPathCfg';

export interface FetchParamsType {
  entity_type: 'mobile' | 'pc' | 'console'; // mobile  pc  console
  search_by_name: string;
  game_code: string[];
  // popularity_range: number[], // 流行度区间
  company: string[]; // 查询公司
  page: number; // 当前页
  page_size: number; // 页尺寸
  order: 'desc' | 'asc'; // 升降序
  order_metric?: string; // 排序指标
  ratio_type: 0 | 1;
  start_release_time: string;
  end_release_time: string;
  // genre: any[], // {"sub": "","main": ""}
  plan: string;
  markets: string[];
  filter_ids: string[];
}

/** 这里处理页面交互逻辑
 * 1 Confirm接口请求
 * 2 lineType请求折线图
 * 3 checkbox可视化柱状图交互|或重新请求折线图
 * 4
 */

const useNewReleaseModel = (props: any) => {
  const {
    entityType,
    topChartsType,
    defaultSelectGameNum = TOPGAMENUM,
    shortLinkValidQuery,
    filterValue,
    setFilterVale,
    hasUrlShortLink,
    // 是否开启默认勾选
    enableDefaultCheck = true,
  } = props || {};

  // console.log('useNewReleaseModel', props);

  const [tableData, setTableData] = useState([]);
  const [tableTotal, setTableTotal] = useState(0);

  // 柱状图数据
  const [barOrPieData, setBarOrPieData] = useState<any[]>([]);
  // 宽表勾选的数据
  const [defaultSelectionGames, setDefaultSelectionGames] = useState<TableSelectionType[]>([]);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);

  const { potentialSteamTopWishLists } = useMobileOptionList();

  const orderMetricCur = useMemo(() => {
    switch (entityType) {
      case 'mobile':
        return 'release_time';
      case 'pc':
        return potentialSteamTopWishLists[0].sortMetric;
      case 'console':
        return 'release_time';
      // return potentialXboxUpcoming.sortMetric;
    }
  }, [entityType]);

  const defaultFetchParams: FetchParamsType = {
    entity_type: entityType,
    search_by_name: '',
    game_code: [],
    // popularity_range: [55, 100],
    company: [],
    page: 1,
    // page_size: 100,
    page_size: 50,
    order: entityType === 'pc' ? 'desc' : 'asc',
    order_metric: orderMetricCur,
    ratio_type: 0,
    start_release_time: moment().add(1, 'days').format('YYYY-MM-DD'),
    end_release_time: moment().add(6, 'M').format('YYYY-MM-DD'),
    plan: 'All',
    markets: [],
    filter_ids: [],
  };

  const [fetchParams, setFetchParams] = useState<FetchParamsType>(defaultFetchParams);
  // 是否重新勾选过游戏
  const [operatedGame, setOperatedGame] = useState(false);
  const [tableListForClearSearch, setTableListForClearSearch] = useState([]);
  const [totalForClearSearch, setTotalForClearSearch] = useState(0);
  const [filterIdsForClearSearch, setFilterIdsForClearSearch] = useState<string[]>([]);

  const fetchTopChartsUP = async (params: FetchParamsType) => {
    if (entityType === 'pc' || entityType === 'console') {
      const paramsTmp = {
        ...params,
        plan: undefined,
        markets: undefined,
      };
      return fetchTopChartsPCConsoleUP({ ...paramsTmp, entity_type: entityType });
    }
    const newParams = tranFormSenorTowerTagParam(params);
    return fetchTopChartsMobileUP({ ...newParams, entity_type: entityType });
  };

  const getGameData = async (argums: {
    params: any;
    isCheckOrSearch: boolean;
    isUpdateLineChart: boolean;
    updateFilterValues: any;
    filterSaveName?: string;
  }) => {
    const {
      params,
      isCheckOrSearch,
      isUpdateLineChart = true,
      updateFilterValues = filterValue,
      filterSaveName,
    } = argums;
    // 没有时间筛选器，直接写token到接口，不用等时间返回
    if (hasUrlShortLink) {
      updateShortLinkFilterId({ [URL_FILTER_KEY]: updateFilterValues });
    }
    try {
      setTableData([]);
      setFetchParams(params);
      const res = await fetchTopChartsUP(params);
      const list = res?.data?.rankList || [];
      const total = res?.data?.total || list?.length; // 搜索不返回total用表格长度计算

      const isSearch = params?.game_code?.length > 0;
      if (params.page === 1) {
        // 过滤rank==0后的游戏可勾选展示可视化
        const filterNoRankGameList = filterGamesWithRank(list);
        /** 默认选择第一页的前6作为表格全选展示 */
        const defaultGames = handleTableDefaultSelectGames(
          filterNoRankGameList?.slice(0, TOPGAMENUM),
          'app_id',
        );
        setDefaultSelectionGames(defaultGames);
        /** 在第一页且没有勾选游戏时默认选中前6（游戏在排序或重新筛选时有更新） */
        if (!isCheckOrSearch) {
          if (enableDefaultCheck) {
            setTableSelection(defaultGames);
          }
          const barOrPieDataTmp = handleTableToBar(filterNoRankGameList);
          setBarOrPieData(barOrPieDataTmp?.slice(0, TOPGAMENUM));
        }
      }
      if (!isSearch) {
        // 有game_code表示是搜索，则不存页面数据
        setTableListForClearSearch(list);
        setTotalForClearSearch(total);
        setFilterIdsForClearSearch(res?.req?.filter_ids || params?.filter_ids || []);
      }

      setTableData(list);
      setTableTotal(total);

      const newFetchParams = {
        ...params,
        // 不用更新后端返回的时间；
        //如果是搜索的时候，则不回填
        filter_ids: isSearch
          ? (params?.game_code?.length ? params.game_code : params?.filter_ids)
          : res?.req?.filter_ids,
      };
      setFetchParams(newFetchParams);

      return res;
    } catch (err) {
      return null;
    }
  };

  const handleTableToBar = (list: any[], end = TOPGAMENUM) => {
    if (!Array.isArray(list)) {
      return [];
    }
    // TODO 将表格数据转化为柱状图数据
    return list.slice(0, end);
  };

  const isNodata = useMemo(() => {
    if (tableTotal < 1 || tableSelection.length < 1) {
      return true;
    }
    return false;
  }, [tableTotal, tableSelection]);

  const handleClearSearch = () => {
    setFetchParams({
      ...fetchParams,
      game_code: [],
      filter_ids: filterIdsForClearSearch,
    });
    setTableData(tableListForClearSearch);
    setTableTotal(totalForClearSearch);
  };

  const transTableDataForSearch = (res: any) => {
    const resultList = res?.data?.rankList;
    if (!Array.isArray(resultList) || !resultList?.length) {
      return [];
    }
    const tranksList: RecordItemType[] = resultList.map((item) => ({
      id: item?.app_id,
      imgSrc: item?.cover,
      lineOneStr: item?.name,
      lineTwoImg: getCountryFlag(item?.publisher_addr),
      lineTwoImgName: item?.publisher_addr,
      // imageType: '',
      lineTwoStr: item?.publisher,
    }));
    return tranksList;
  };

  return {
    // tableLoading,
    // chartLoading,
    tableTotal,
    tableData,
    barOrPieData,
    setBarOrPieData,
    fetchParams,
    defaultFetchParams,
    getGameData,
    tableSelection,
    defaultSelectionGames,
    operatedGame,
    setOperatedGame,
    isNodata,
    setTableSelection,
    handleClearSearch,
    transTableDataForSearch,
  };
};

export default useNewReleaseModel;
